import { safeError } from '@/lib/safeLog';

/**
 * Clear all auth-related browser storage on sign-out. Shared between the
 * Supabase and Clerk auth providers (REK-1985 / SSO.4) so the sign-out
 * semantics can't drift: Supabase token keys (`sb-*` / `*supabase*`) plus the
 * user-scoped `reklaim_*` keys are removed; `reklaim_cookie_consent` is
 * device-level consent and MUST persist across sign-outs.
 *
 * Clerk's own session state (cookies + `__clerk*` storage) is intentionally
 * NOT touched here — `clerk.signOut()` owns that lifecycle.
 */
export function clearAuthLocalStorage(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('supabase') || key.includes('sb-'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));

    // Also clear user-specific reklaim keys that shouldn't persist across sign out
    // Note: reklaim_cookie_consent is device-level consent and should persist
    localStorage.removeItem('reklaim_email');
    localStorage.removeItem('reklaim_dob');
    localStorage.removeItem('reklaim_first_name');
    localStorage.removeItem('reklaim_last_name');
    localStorage.removeItem('reklaim_welcome_shown');
    localStorage.removeItem('reklaim_ruby_intro_seen');
    localStorage.removeItem('reklaim_attribution');
    localStorage.removeItem('reklaim_terms_accepted_at');
    // REK-2137: the pivot's Google-signup affirmation handoff — single-use,
    // consumed by /auth/sso-callback; swept here so an abandoned OAuth
    // attempt can never leave a stale affirmation behind a sign-out.
    localStorage.removeItem('reklaim_sso_terms_accepted_at');
    // REK-1727: prevent stale-flag bug — wipe the marketing-consent
    // flag on sign-out so a later non-consenting signup on the same
    // browser can never read a leftover 'true'.
    localStorage.removeItem('reklaim_marketing_consent');
  } catch (e) {
    safeError('Error clearing auth storage:', e);
  }
}
